import { defineStore } from 'pinia';
import api from '@/api/axiosBase';
import { getAuth } from 'firebase/auth';

export interface ProjectListItem {
  simpleID: string;
  projectName: string;
  siteUrl?: string;
  subscriptionPlan?: string;
  createdAt: string;
  hasShopify: boolean;
  hasSendgrid: boolean;
  hasTwilio: boolean;
  templateCount: number;
  teamCount: number;
}

// How long the cache is considered "fresh" (no background fetch needed)
const CACHE_FRESH_MS = 60_000; // 60 seconds

// How long the cache is considered "usable" for instant display (stale-while-revalidate)
// Beyond this, we force a full reload with loading state
const CACHE_MAX_AGE_MS = 10 * 60_000; // 10 minutes

const STORAGE_KEY = 'myuser_projects_cache';

// ── Persistence helpers ──────────────────────────────────────────────
interface PersistedCache {
  projects: ProjectListItem[];
  lastFetched: number;
  userName: string;
  userEmail: string;
}

function loadFromStorage(): PersistedCache | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedCache;
    if (parsed && Array.isArray(parsed.projects) && parsed.lastFetched > 0) {
      return parsed;
    }
  } catch {
    // corrupt data — ignore
  }
  return null;
}

function saveToStorage(cache: PersistedCache) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // storage full or unavailable — ignore
  }
}

function clearStorage() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ── Mapping ──────────────────────────────────────────────────────────
function normalizePlan(plan?: string): string {
  if (!plan || plan.toLowerCase() === 'none' || plan.toLowerCase() === 'n/a') {
    return 'Free';
  }
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

function mapProjectResponse(proj: Record<string, unknown>): ProjectListItem {
  const teamates = proj.teamates as Array<unknown> | undefined;
  const sendgridDesigns = proj.sendgridDesigns as Array<unknown> | undefined;

  return {
    simpleID:
      (proj.simpleID as string) ||
      (proj.SimpleID as string) ||
      (proj.simpleId as string) ||
      '',
    projectName: (proj.projectName as string) || '',
    siteUrl: proj.siteUrl as string,
    subscriptionPlan: normalizePlan(
      (proj.subscription as Record<string, unknown>)?.plan as string
    ),
    createdAt: proj.createdAt as string,
    hasShopify: Boolean(proj.siteUrl),
    hasSendgrid: Boolean(proj.sendgridKey || proj.hasSendgrid),
    hasTwilio: Boolean(proj.twilioKey || proj.hasTwilio),
    templateCount:
      (proj.templateCount as number) ?? sendgridDesigns?.length ?? 0,
    teamCount: (proj.teamCount as number) ?? teamates?.length ?? 1
  };
}

// ── Hydrate from sessionStorage on app start ─────────────────────────
const saved = loadFromStorage();

export const useProjectsStore = defineStore('projects', {
  state: () => ({
    projects: (saved?.projects ?? []) as ProjectListItem[],
    lastFetched: saved?.lastFetched ?? 0,
    loading: false,
    refreshing: false,
    error: null as string | null,
    userName: saved?.userName ?? '',
    userEmail: saved?.userEmail ?? ''
  }),

  getters: {
    hasCachedData(): boolean {
      return this.projects.length > 0 && this.lastFetched > 0;
    },
    isFresh(): boolean {
      return Date.now() - this.lastFetched < CACHE_FRESH_MS;
    },
    isExpired(): boolean {
      return Date.now() - this.lastFetched > CACHE_MAX_AGE_MS;
    }
  },

  actions: {
    /**
     * Main entry point — stale-while-revalidate:
     * - Fresh (< 60s): show cache, skip network
     * - Stale (60s–10min): show cache instantly, refresh silently in background
     * - Expired (> 10min) or empty: loading skeleton, full fetch
     */
    async loadProjects() {
      if (this.hasCachedData && this.isFresh) {
        return; // instant, no network
      }
      if (this.hasCachedData && !this.isExpired) {
        this.refreshInBackground(); // instant display, silent refresh
        return;
      }
      await this.fetchProjects(); // skeleton → fetch
    },

    async fetchProjects() {
      this.loading = true;
      this.error = null;
      try {
        await this._doFetch();
      } catch (e: unknown) {
        this.error = e instanceof Error ? e.message : 'Failed to load projects';
        console.error('Error fetching projects:', e);
      } finally {
        this.loading = false;
      }
    },

    async refreshInBackground() {
      if (this.refreshing) return;
      this.refreshing = true;
      try {
        await this._doFetch();
      } catch (e) {
        console.warn('Background project refresh failed:', e);
      } finally {
        this.refreshing = false;
      }
    },

    async forceRefresh() {
      this.lastFetched = 0;
      await this.fetchProjects();
    },

    async _doFetch() {
      const auth = getAuth();
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) return;

      const response = await api.post(
        '/v2/agent/auth',
        { Extras: [] },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200) {
        const data = response.data;

        if (data.Agent?.displayName) {
          this.userName = data.Agent.displayName;
        }
        if (data.Agent?.email) {
          this.userEmail = data.Agent.email;
        }

        if (Array.isArray(data.Projects)) {
          this.projects = data.Projects.filter(
            (proj: Record<string, unknown>) =>
              !proj.isDemo &&
              !proj.demoSourceTenant &&
              !String(
                (proj.simpleID as string) ||
                  (proj.SimpleID as string) ||
                  (proj.simpleId as string) ||
                  ''
              ).startsWith('DEMO')
          )
            .map((proj: Record<string, unknown>) => mapProjectResponse(proj))
            .filter((p: ProjectListItem) => p.simpleID)
            .sort(
              (a: ProjectListItem, b: ProjectListItem) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );
        }

        this.lastFetched = Date.now();

        // Persist to sessionStorage so it survives page reloads
        this._persist();
      }
    },

    addProject(proj: ProjectListItem) {
      this.projects.unshift(proj);
      this._persist();
    },

    clearCache() {
      this.projects = [];
      this.lastFetched = 0;
      this.userName = '';
      this.userEmail = '';
      this.error = null;
      clearStorage();
    },

    _persist() {
      saveToStorage({
        projects: this.projects,
        lastFetched: this.lastFetched,
        userName: this.userName,
        userEmail: this.userEmail
      });
    }
  }
});
